import React, { useEffect, useMemo } from "react";
import {
  Bike,
  ChefHat,
  Copy,
  DollarSign,
  Pencil,
  Shield,
  SlidersHorizontal,
  Trash2,
  UserCog,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";

import { COLORS } from "../../../constants";
import {
  getScreenLabel,
  RESTRICTED_SCREEN_KEYS,
  SCREEN_CATEGORIES,
} from "../../../constants/screenCatalogCategories";
import {
  hasFullAccessRoleNome,
  isAdministradorRoleNome,
  isDesenvolvedorRoleNome,
} from "../../../lib/screenAccess";
import { useAuth } from "../../../context/AuthContext";
import type { RoleAdminListItem } from "../../../services/roleAdminService";

function roleIcon(nome: string): LucideIcon {
  const n = nome.toLowerCase();
  if (n.includes("admin")) return Shield;
  if (n.includes("entreg")) return Bike;
  if (n.includes("garçom") || n.includes("garcom")) return UtensilsCrossed;
  if (n.includes("caixa")) return DollarSign;
  if (n.includes("pizza")) return ChefHat;
  return UserCog;
}

type Props = {
  role: RoleAdminListItem;
  lojasById: Map<number, string>;
  onEdit: () => void;
  onClone: () => void;
  onViewUsers: () => void;
  onDelete: () => void;
};

const RoleListCard: React.FC<Props> = ({
  role,
  lojasById,
  onEdit,
  onClone,
  onViewUsers,
  onDelete,
}) => {
  const Icon = roleIcon(role.nome);

  const lojasLabel = role.aplicaTodasLojas
    ? "Todas as lojas"
    : role.lojaIds
        .map((id) => lojasById.get(id))
        .filter(Boolean)
        .join(", ") || "Sem loja definida";

  return (
    <article className="rounded-xl border border-neutral-200 bg-white p-3 shadow-sm transition hover:border-neutral-300">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${COLORS.PRIMARY}14` }}
          >
            <Icon size={18} style={{ color: COLORS.PRIMARY }} />
          </div>
          <h3 className="truncate text-sm font-semibold text-neutral-900">
            {role.nome}
            {role.protegido && (
              <span className="ml-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                Sistema
              </span>
            )}
          </h3>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={onViewUsers}
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label={`Ver usuários de ${role.nome}`}
            title="Ver usuários vinculados"
          >
            <Users size={15} />
          </button>
          <button
            type="button"
            onClick={onClone}
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label={`Clonar ${role.nome}`}
            title="Clonar perfil"
          >
            <Copy size={15} />
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label={`Editar ${role.nome}`}
            title="Editar"
          >
            <Pencil size={15} />
          </button>
          {!role.protegido && (
            <button
              type="button"
              onClick={onDelete}
              className="rounded-md p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600"
              aria-label={`Excluir ${role.nome}`}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      <p className="mt-1.5 line-clamp-1 text-xs text-neutral-600">
        {role.descricao || "Sem descrição"}
      </p>

      <div className="mt-2 space-y-0.5 text-[11px] text-neutral-500">
        <button
          type="button"
          onClick={onViewUsers}
          className="block font-medium uppercase tracking-wide hover:text-primary"
          title="Ver usuários vinculados"
        >
          {role.usuarioCount}{" "}
          {role.usuarioCount === 1 ? "usuário" : "usuários"}
        </button>
        <p className="inline-flex items-center gap-1 normal-case">
          <SlidersHorizontal size={12} />
          {role.modulosAtivos}/{role.modulosTotal} módulos
        </p>
        <p className="truncate normal-case" title={lojasLabel}>
          {lojasLabel}
        </p>
        {role.podeVerTodasLojas && (
          <p className="normal-case text-primary">Visão multi-loja</p>
        )}
        {role.podePriorizarMontagem && (
          <p className="normal-case text-primary">Priorizar montagem</p>
        )}
        {role.podeVerMaisInfoFichaProducao && (
          <p className="normal-case text-primary">
            Mais informações na ficha de produção
          </p>
        )}
      </div>
    </article>
  );
};

export type RoleFormState = {
  nome: string;
  descricao: string;
  aplicaTodasLojas: boolean;
  podeVerTodasLojas: boolean;
  podePriorizarMontagem: boolean;
  podeVerMaisInfoFichaProducao: boolean;
  lojaIds: number[];
  screenKeys: Set<string>;
};

export function emptyRoleForm(): RoleFormState {
  return {
    nome: "",
    descricao: "",
    aplicaTodasLojas: true,
    podeVerTodasLojas: false,
    podePriorizarMontagem: false,
    podeVerMaisInfoFichaProducao: false,
    lojaIds: [],
    screenKeys: new Set(["meu_perfil"]),
  };
}

type ModalProps = {
  isOpen: boolean;
  mode: "create" | "edit" | "clone";
  editingRole: RoleAdminListItem | null;
  lojas: Array<{ id: number; nome: string }>;
  form: RoleFormState;
  isSaving: boolean;
  onClose: () => void;
  onChange: (form: RoleFormState) => void;
  onSubmit: () => void;
};

export const RolePermissionModal: React.FC<ModalProps> = ({
  isOpen,
  mode,
  editingRole,
  lojas,
  form,
  isSaving,
  onClose,
  onChange,
  onSubmit,
}) => {
  const { user } = useAuth();
  const isDesenvolvedor = isDesenvolvedorRoleNome(user?.role?.nome);
  const isAdministrador = isAdministradorRoleNome(user?.role?.nome);
  const canManageAllPermissions = isAdministrador || isDesenvolvedor;
  const isProtected =
    mode === "edit" &&
    (editingRole?.protegido ?? false) &&
    !canManageAllPermissions;
  const canEditRestricted =
    hasFullAccessRoleNome(form.nome) || canManageAllPermissions;

  const selectedCount = form.screenKeys.size;
  const totalCount = useMemo(
    () => SCREEN_CATEGORIES.reduce((acc, c) => acc + c.screenKeys.length, 0),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleScreen = (key: string) => {
    if (isProtected) return;
    if (
      RESTRICTED_SCREEN_KEYS.includes(
        key as (typeof RESTRICTED_SCREEN_KEYS)[number]
      ) &&
      !canEditRestricted
    ) {
      return;
    }
    const next = new Set(form.screenKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange({ ...form, screenKeys: next });
  };

  const toggleCategory = (keys: string[]) => {
    if (isProtected) return;
    const editable = keys.filter(
      (k) =>
        !RESTRICTED_SCREEN_KEYS.includes(
          k as (typeof RESTRICTED_SCREEN_KEYS)[number]
        ) || canEditRestricted
    );
    const allSelected = editable.every((k) => form.screenKeys.has(k));
    const next = new Set(form.screenKeys);
    if (allSelected) editable.forEach((k) => next.delete(k));
    else editable.forEach((k) => next.add(k));
    onChange({ ...form, screenKeys: next });
  };

  const toggleLoja = (lojaId: number) => {
    const next = new Set(form.lojaIds);
    if (next.has(lojaId)) next.delete(lojaId);
    else next.add(lojaId);
    onChange({
      ...form,
      aplicaTodasLojas: false,
      lojaIds: Array.from(next),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fechar"
        onClick={onClose}
      />
      <div className="relative flex max-h-[96vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between gap-4 border-b px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-neutral-900">
              {mode === "edit"
                ? "Editar permissão"
                : mode === "clone"
                  ? "Clonar permissão"
                  : "Nova permissão"}
            </h2>
            <p className="truncate text-xs text-neutral-500">
              {mode === "clone"
                ? "Revise antes de criar o novo perfil"
                : "Nome, lojas e módulos acessíveis"}
            </p>
          </div>
          <span className="shrink-0 text-xs text-neutral-500">
            {selectedCount}/{totalCount} módulos
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-neutral-700">
                Nome
              </label>
              <input
                type="text"
                value={form.nome}
                disabled={isProtected}
                onChange={(e) => onChange({ ...form, nome: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-neutral-50"
                placeholder="Ex.: Garçom"
              />
            </div>
            <div className="lg:col-span-3">
              <label className="mb-1 block text-xs font-medium text-neutral-700">
                Descrição
              </label>
              <input
                type="text"
                value={form.descricao}
                onChange={(e) =>
                  onChange({ ...form, descricao: e.target.value })
                }
                className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Breve descrição do perfil"
              />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-neutral-200 p-2.5">
              <p className="mb-2 text-xs font-medium text-neutral-700">
                Atribuição por loja
              </p>
              <label className="flex items-center gap-2 text-xs text-neutral-800">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={form.aplicaTodasLojas}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      aplicaTodasLojas: e.target.checked,
                      lojaIds: e.target.checked ? [] : form.lojaIds,
                    })
                  }
                />
                Todas as lojas
              </label>
              {!form.aplicaTodasLojas && (
                <div className="mt-2 space-y-1">
                  {lojas.map((loja) => (
                    <label
                      key={loja.id}
                      className="flex items-center gap-2 text-xs text-neutral-800"
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-primary"
                        checked={form.lojaIds.includes(loja.id)}
                        onChange={() => toggleLoja(loja.id)}
                      />
                      <span className="truncate">{loja.nome}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 sm:col-span-1 lg:col-span-3">
              <label className="flex items-start gap-2 text-xs text-neutral-800">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                  checked={form.podeVerTodasLojas}
                  disabled={isProtected}
                  onChange={(e) =>
                    onChange({ ...form, podeVerTodasLojas: e.target.checked })
                  }
                />
                <span>
                  <span className="font-medium text-neutral-900">
                    Ver conteúdo de todas as lojas
                  </span>
                  <span className="mt-0.5 block text-[11px] text-neutral-600">
                    Sobrescreve a loja vinculada ao usuário no cadastro.
                  </span>
                </span>
              </label>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 sm:col-span-1 lg:col-span-3">
              <label className="flex items-start gap-2 text-xs text-neutral-800">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                  checked={form.podePriorizarMontagem}
                  disabled={isProtected}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      podePriorizarMontagem: e.target.checked,
                    })
                  }
                />
                <span>
                  <span className="font-medium text-neutral-900">
                    Priorizar pedidos na montagem
                  </span>
                  <span className="mt-0.5 block text-[11px] text-neutral-600">
                    Permite priorizar pedidos na fila do pizzaiolo (Painel e
                    Sequência).
                  </span>
                </span>
              </label>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 sm:col-span-1 lg:col-span-3">
              <label className="flex items-start gap-2 text-xs text-neutral-800">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                  checked={form.podeVerMaisInfoFichaProducao}
                  disabled={isProtected}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      podeVerMaisInfoFichaProducao: e.target.checked,
                    })
                  }
                />
                <span>
                  <span className="font-medium text-neutral-900">
                    Ver mais informações na ficha de produção
                  </span>
                  <span className="mt-0.5 block text-[11px] text-neutral-600">
                    Mostra base, sobra e observação da sobra no turno do dia.
                    Na conferência da noite esses campos continuam visíveis.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {(isProtected ||
            (canManageAllPermissions &&
              editingRole?.protegido &&
              !isProtected)) && (
            <div className="mt-3">
              {isProtected && (
                <p className="rounded-md bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                  Perfil de sistema — acesso definido pelo servidor.
                </p>
              )}
              {canManageAllPermissions &&
                editingRole?.protegido &&
                !isProtected && (
                <p className="rounded-md bg-blue-50 px-2.5 py-1.5 text-[11px] text-blue-800">
                  Como administrador ou desenvolvedor, você pode editar este
                  perfil de sistema.
                </p>
                )}
            </div>
          )}

          <div className="mt-3">
            <p className="mb-2 text-xs font-medium text-neutral-700">
              Módulos acessíveis
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {SCREEN_CATEGORIES.map((category) => {
                const editableKeys = category.screenKeys.filter(
                  (k) =>
                    !RESTRICTED_SCREEN_KEYS.includes(
                      k as (typeof RESTRICTED_SCREEN_KEYS)[number]
                    ) || canEditRestricted
                );
                const allSelected =
                  editableKeys.length > 0 &&
                  editableKeys.every((k) => form.screenKeys.has(k));
                const someSelected = editableKeys.some((k) =>
                  form.screenKeys.has(k)
                );

                return (
                  <div
                    key={category.key}
                    className="rounded-lg border border-neutral-200 p-2"
                  >
                    <label className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-primary"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = someSelected && !allSelected;
                          }
                        }}
                        disabled={isProtected}
                        onChange={() => toggleCategory(category.screenKeys)}
                      />
                      <span className="truncate">{category.label}</span>
                    </label>
                    <div className="space-y-0.5">
                      {category.screenKeys.map((key) => {
                        const restricted =
                          RESTRICTED_SCREEN_KEYS.includes(
                            key as (typeof RESTRICTED_SCREEN_KEYS)[number]
                          ) && !canEditRestricted;
                        return (
                          <label
                            key={key}
                            className={`flex items-start gap-1.5 rounded px-1 py-0.5 text-[11px] leading-tight ${
                              restricted
                                ? "text-neutral-400"
                                : "text-neutral-800"
                            }`}
                            title={getScreenLabel(key)}
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary"
                              checked={form.screenKeys.has(key)}
                              disabled={isProtected || restricted}
                              onChange={() => toggleScreen(key)}
                            />
                            <span className="line-clamp-2">
                              {getScreenLabel(key)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t px-4 py-3 sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-neutral-600 hover:text-neutral-900"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={onSubmit}
            className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white shadow disabled:opacity-60"
            style={{ backgroundColor: COLORS.PRIMARY }}
          >
            {isSaving
              ? "Salvando…"
              : mode === "edit"
                ? "Salvar"
                : mode === "clone"
                  ? "Criar cópia"
                  : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleListCard;
